import { AttributionScope } from "@redotech/redo-attribution-common/attribution-scope";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/** Attributed orders for one marketing entity, in the team's own currency.
 *  Orders in another currency are excluded from both numbers. */
export const attributedMetricSchema = z.object({
  revenue: z.number(),
  orders: z.number(),
});
export type AttributedMetric = z.infer<typeof attributedMetricSchema>;

/** Attributed order metrics keyed by the grouped scope's id (campaign id, etc.). */
export type Attribution = Record<string, AttributedMetric>;

/** One grouped result: the id each requested scope took, plus its metrics. An
 *  empty `scopes` is the team total, which is a single row. */
export const attributionRowSchema = attributedMetricSchema.extend({
  scopes: z.partialRecord(z.enum(AttributionScope), z.string().nullable()),
  /** The sales' subtype when the read splits by it, else null. */
  outcomeSubtype: z.string().nullable(),
  /** The label the read gave the sale's subject, else null. */
  subjectLabel: z.string().nullable(),
  /** First day of the bucket, or null when the query did not bucket. */
  bucket: zExt.plainDate().nullable(),
});
export type AttributionRow = z.infer<typeof attributionRowSchema>;

export const attributionRowsSchema = z.array(attributionRowSchema);

/** Index rows by one scope's id, summing rows that share it so a grouping finer
 *  than the index cannot undercount. A row the scope is null for is dropped. */
export function attributionByScope(
  rows: readonly AttributionRow[],
  scope: AttributionScope,
): Attribution {
  const totals = new Map<string, AttributedMetric>();
  for (const row of rows) {
    const id = row.scopes[scope];
    if (id == null) {
      continue;
    }
    const running = totals.get(id);
    totals.set(id, {
      revenue: (running?.revenue ?? 0) + row.revenue,
      orders: (running?.orders ?? 0) + row.orders,
    });
  }
  return Object.fromEntries(totals);
}
